import { nhost } from './nhost'

/**
 * nhost/storage buckets (created in hasura/migrations/default/…_storage_buckets
 * and …_instagram). `boat-photos` can be fetched by anyone who knows a file's
 * id — the NautiX feed hands portals those URLs — and so can `social-posts`,
 * the slides Instagram fetches while a post is published, and `catalogs`, the
 * Gebrauchtyachtkatalog the website downloads. Everything else is private.
 */
export type Bucket = 'default' | 'boat-photos' | 'social-posts' | 'catalogs'

/** Which bucket an upload belongs in. Only a boat's images are public. */
export const bucketFor = (relation: string | undefined, mimeType: string | undefined): Bucket =>
  relation === 'boats' && (mimeType ?? '').startsWith('image/') ? 'boat-photos' : 'default'

export interface UploadedFile {
  /** The file id — what attachments.uri stores. */
  key: string
  ContentType: string
  /**
   * MD5 of the stored bytes (storage's ETag), for attachments.md5: the NautiX
   * feed leaves out any image without one.
   */
  md5: string | null
}

export const fileStorage = {
  /** Upload one file. Returns the shape the callers already used with HBP. */
  put: async (file: File, bucket: Bucket = 'default'): Promise<UploadedFile> => {
    const { body } = await nhost.storage.uploadFiles({ 'bucket-id': bucket, 'file[]': [file] })
    const uploaded = body.processedFiles?.[0]
    if (!uploaded?.id) {
      throw new Error('upload failed')
    }
    const md5 = String(uploaded.etag ?? '').replace(/"/g, '')
    return {
      key: uploaded.id,
      ContentType: uploaded.mimeType ?? file.type,
      md5: /^[0-9a-f]{32}$/i.test(md5) ? md5.toLowerCase() : null,
    }
  },
  /** Delete one file. */
  remove: async (fileId: string): Promise<void> => {
    await nhost.storage.deleteFile(fileId)
  },
}

export type FileStorage = typeof fileStorage

// A file id is a UUID. Anything else in attachments.uri is an HBP object key
// that scripts/migrate-hbp-storage.mjs has not converted yet.
const FILE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const presigned = new Map<string, { url: string; expires: number }>()

/**
 * A URL the browser can load without sending a token — an <img>, a download
 * link. Presigned by nhost/storage and valid for the bucket's
 * download_expiration (15 minutes); cached until shortly before then.
 */
export const fileUrl = async (fileId: string | undefined | null): Promise<string | null> => {
  if (!fileId || !FILE_ID.test(fileId)) {
    return null
  }
  const cached = presigned.get(fileId)
  if (cached && cached.expires > Date.now()) {
    return cached.url
  }
  const { body } = await nhost.storage.getFilePresignedURL(fileId)
  // Re-request a minute early, so a URL is never handed out about to expire.
  presigned.set(fileId, {
    url: body.url,
    expires: Date.now() + Math.max(0, body.expiration - 60) * 1000,
  })
  return body.url
}
